import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '../ui/loading-screen';
import { config } from '@/lib/config';
import { useNavigate } from 'react-router-dom';
import { ApiErrorPage } from '@/pages/auth/ApiErrorPage';
import { STORAGE_KEYS } from '@/lib/constants/storage-keys';
import { logger } from '@/lib/utils/logger';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserBotData = Record<string, any> & {
  api_key?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  setUser: React.Dispatch<React.SetStateAction<User | null>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  isInitialized: boolean
  retryInitialization: () => Promise<boolean>
  userBotData: UserBotData | null
  setUserBotData: React.Dispatch<React.SetStateAction<UserBotData | null>>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  setUser: () => { },
  setLoading: () => { },
  setError: () => { },
  isInitialized: false,
  retryInitialization: async () => false,
  userBotData: null,
  setUserBotData: () => { },
});

type SessionLike = {
  access_token?: string
  user?: User | null
}

type InitializationState = {
  accessToken: string
  promise: Promise<UserBotData>
}

let activeInitialization: InitializationState | null = null
let initializedAccessToken: string | null = null
let initializedUserBotData: UserBotData | null = null

class AuthInitializationError extends Error {
  status: number
  detail: string

  constructor(status: number, detail: string) {
    super(detail || `Failed to initialize user (${status})`)
    this.name = 'AuthInitializationError'
    this.status = status
    this.detail = detail
  }
}

async function getResponseDetail(response: Response) {
  const body = await response.text().catch(() => '')

  if (!body) {
    return response.statusText || 'Unknown API error'
  }

  try {
    const parsed = JSON.parse(body) as { detail?: unknown }
    if (typeof parsed.detail === 'string') {
      return parsed.detail
    }
  } catch {
    // Fall back to the raw body when the API does not return JSON.
  }

  return body
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message
  }

  return 'Unknown error'
}

function getErrorName(error: unknown) {
  if (error instanceof Error) {
    return error.name
  }

  return 'UnknownError'
}

async function initializeUser(supabaseToken: string) {
  const response = await fetch(`${config.apiURL}/auth/supabase/initialize_user`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${supabaseToken}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const detail = await getResponseDetail(response)
    console.error('initializeUser: Response not OK', response.status, detail);
    throw new AuthInitializationError(response.status, detail);
  }
  const jsonData = await response.json() as UserBotData;
  return jsonData;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentSession, setCurrentSession] = useState<SessionLike | null>(null);
  const [userBotData, setUserBotData] = useState<UserBotData | null>(null);
  const initializationStarted = useRef(false);
  const lastHandledAccessToken = useRef<string | null>(null)

  const navigate = useNavigate();

  const clearAuthState = async () => {
    await supabase.auth.signOut({ scope: 'local' }).catch((signOutError: unknown) => {
      logger.warn('[Auth] Failed to clear local Supabase session', signOutError)
    });
    localStorage.removeItem('auth_redirect_path');
    localStorage.removeItem(STORAGE_KEYS.NEXUS_SECRET);
    setError(null);
    setUser(null);
    setUserBotData(null);
    setCurrentSession(null);
    setIsInitialized(false);
    lastHandledAccessToken.current = null;
    initializedAccessToken = null;
    initializedUserBotData = null;
  }

  // Debug: Log OAuth return state
  console.log('[AuthProvider] Mount/render. URL:', window.location.href);
  console.log('[AuthProvider] localStorage auth_redirect_path:', localStorage.getItem('auth_redirect_path'));

  const getSharedInitialization = (accessToken: string) => {
    if (initializedAccessToken === accessToken && initializedUserBotData) {
      return Promise.resolve(initializedUserBotData)
    }

    if (activeInitialization?.accessToken === accessToken) {
      return activeInitialization.promise
    }

    const initializationPromise = initializeUser(accessToken)
      .then((responseData) => {
        initializedAccessToken = accessToken
        initializedUserBotData = responseData
        return responseData
      })
      .finally(() => {
        if (activeInitialization?.accessToken === accessToken) {
          activeInitialization = null
        }
      })

    activeInitialization = {
      accessToken,
      promise: initializationPromise,
    }

    return initializationPromise
  }

  const handleInitializeUser = async (session: SessionLike | null): Promise<'SUCCESS' | 'FAILED' | 'SKIPPED'> => {
    const accessToken = session?.access_token

    if (!accessToken) {
      return 'FAILED'
    }

    if (initializationStarted.current) {
      return 'SKIPPED';
    }

    initializationStarted.current = true;

    try {
      const responseData = await getSharedInitialization(accessToken);
      setUserBotData(responseData);
      lastHandledAccessToken.current = accessToken

      // Save the API key to localStorage for use in iframe messaging
      if (responseData.api_key) {
        logger.log('[Auth] Saving API key to localStorage')
        localStorage.setItem(STORAGE_KEYS.NEXUS_SECRET, responseData.api_key)
      } else {
        logger.warn('[Auth] No API key found in initialization response');
      }

      setIsInitialized(true);
      setError(null);
      return 'SUCCESS';
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error)
      console.error('handleInitializeUser: Error initializing user:', errorMessage, getErrorName(error), error);
      if (error instanceof AuthInitializationError) {
        await clearAuthState()

        if (error.status === 403) {
          navigate('/uninvited', { replace: true });
        } else if (error.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError('Unable to initialize your account. Please try again.')
        }
      } else {
        // For other errors (network issues, server down, etc), show the API error page
        setError('Unable to connect to the API. Please check your connection and try again.')
      }
      return 'FAILED';
    } finally {
      initializationStarted.current = false;
    }
  };

  const retryInitialization = async () => {
    if (currentSession) {
      setLoading(true);
      setError(null);
      setIsInitialized(false);

      const initStatus = await handleInitializeUser(currentSession);

      if (initStatus === 'SUCCESS') {
        setUser(currentSession.user ?? null);
        // isInitialized is set to true within handleInitializeUser upon success
        setLoading(false);
        return true;
      } else {
        // For 'FAILED' or 'SKIPPED' (though SKIPPED is unlikely here if called manually)
        // setUser(null), setError, setIsInitialized(false) should be handled by handleInitializeUser on 'FAILED'
        setLoading(false);
        return false;
      }
      // No separate catch block needed if handleInitializeUser handles its errors and doesn't throw them to here.
    } else {
      // retryInitialization: No current session, returning false
    }
    return false;
  };

  const handleUser = async (user: User | null, session: SessionLike | null) => {
    if (user) {
      const accessToken = session?.access_token || null

      if (
        accessToken &&
        lastHandledAccessToken.current === accessToken &&
        initializedAccessToken === accessToken &&
        initializedUserBotData &&
        isInitialized
      ) {
        setCurrentSession(session);
        setUserBotData(initializedUserBotData);
        setUser(user);
        setError(null);
        if (loading) setLoading(false);
        return;
      }

      // Only proceed if there's a change in user ID or session
      if (!currentSession?.user?.id || currentSession.user.id !== user.id || !isInitialized) {
        logger.log('User state:', user)
        logger.log('User metadata:', user.user_metadata)
        logger.log('App metadata:', user.app_metadata)

        setCurrentSession(session);

        const initStatus = await handleInitializeUser(session);

        if (initStatus === 'SUCCESS') {
          setUser(user); // Set the Supabase user object
          // isInitialized is set by handleInitializeUser
          setLoading(false); // Only set loading false if this call successfully initialized
        } else if (initStatus === 'FAILED') {
          // setUser(null), setError, setIsInitialized(false) are handled by handleInitializeUser
          setLoading(false); // Also set loading false if this call resulted in a failure
        } else if (initStatus === 'SKIPPED') {
          // DO NOT set user or loading here. The active instance will handle it.
          // setLoading(true) was called at the start of this conditional block, so it remains true.
        }
      } else {
        // If user is already initialized and matches, loading should be false.
        // This case might be hit if onAuthStateChange fires for an already handled user.
        if (loading) setLoading(false);
      }
    } else {
      setUser(null);
      setIsInitialized(false);
      setUserBotData(null);
      setCurrentSession(null);
      lastHandledAccessToken.current = null;
      initializedAccessToken = null;
      initializedUserBotData = null;
      setLoading(false); // If no user, we are not loading.
    }
    // The general setLoading(false) at the end is removed. It's now conditional.
    // logger.log('[DEBUG] handleUser: End of function. Current state - loading:', loading, 'user:', !!user, 'isInitialized:', isInitialized);
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleUser(session?.user || null, session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      handleUser(session?.user || null, session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);


  if (loading && !error && !isInitialized) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ApiErrorPage message={error} onRetry={retryInitialization} />;
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading, // This loading reflects Supabase loading AND backend init loading
      error,
      setUser,
      setLoading,
      setError,
      isInitialized,
      retryInitialization,
      userBotData,
      setUserBotData
    }}>
      {children}
    </AuthContext.Provider>
  )
}
